import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import CommonAggridTable from '../../common/AgGrid/CommonAggridTable.jsx';
import ShowTaskLink from '../show/ShowTaskLink.jsx';
import { useTaskOptions } from '../hooks/useTaskOptions.js';
import ShowWorkPackageLink from '../show/ShowWorkPackageLink.jsx';
import TooltipWithRef from '../../common/Tooltip.jsx';

import {
  project_entry_path,
  task_list_json_path
} from '~/routes';

const TaskLinkCellRenderer = ({params}) => {
    const triggerLabel = params.getValue();
    const primaryTitle = ((params.data || {})['identifier'] || {})['label'];
    const permissions = params.context?.permissions || {};
    const advancedFeatures = params.context?.advancedFeatures;
    const programmeId = params.context?.programmeId;
    const projectId = params.context?.projectId || ((params.data || {})['project_identifier'] || {})['id'];
    const secondaryTitle = ((params.data || {})['title'] || {})['label'];
    if (params.node.group) return null;
    return (
      <ShowTaskLink
        identifier={primaryTitle}
        triggerLabel={triggerLabel}
        label={secondaryTitle}
        programmeId={programmeId}
        projectId={projectId}
        permissions={permissions}
        advancedFeatures={advancedFeatures}
      />
    );
};

export const ProjectTaskIdCellRenderer = memo((params) => {
  return <TaskLinkCellRenderer params={params} />
});

export const ProjectTitleCellRenderer = memo((params) => {
  const value = params.getValue();
  return <div style={{maxWidth: '100%'}}>
    <TooltipWithRef
      trigger='hover'
      title={value}
      style={{
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      <TaskLinkCellRenderer params={params} />
    </TooltipWithRef>
  </div>
});

export const ProjectStatusCellRenderer = memo((params) => {
    const label = params.valueFormatted || params.getValue();
    const status = ((params.data || {})[params.colDef.colId] || {})["status"];
    return (
        <span className={`label task-status ${status}`}>{label}</span>
    );
})

const ProjectLinkCellRenderer = memo((params) => {
  const project_id = ((params.data || {})[params.colDef.colId] || {})["id"];
  const href = project_id && project_entry_path({project_id: project_id, not_found_path: window.location.pathname});
  const label = params.getValue();
  return (
      href ? <a href={href} data-turbo={false}>{label}</a> : (label || "")
  );
});

export const valueGetter = (params) => {
  params.data ||= {};
  if (params.node.group) {

    const containsClientSideData = 
      params.api.getGridOption('rowModelType') === 'serverSide' ||
      params.api.getRowGroupColumns().length === 0;
    return containsClientSideData
      ? params.data.work_package?.[params.colDef.colId]
      : params.node.allLeafChildren?.[0]?.data?.work_package?.[params.colDef.colId];
  }
  return (params.data[params.colDef.colId] || {})["label"];
};

const selectValueGetter = (params) => {
  params.data ||= {};

  const colId = params.colDef?.colId;
  const cell = params.data[colId];
  const opts = cell?.['options'];

  if (Array.isArray(opts)) {
    return opts.find(o => o?.['value'] === cell?.['label'])?.['label'] ?? cell?.['label'];
  }

  const map = params.colDef?.filterParams?.optionMap;
  return map?.get(cell)?.['label'] ?? map?.get(cell) ?? cell;
};

const multiSelectValueGetter = (params) => {
  params.data ||= {};

  const cell = params.data[params.colDef.colId] || {};
  const options = cell['options'];
  const value = cell['label'];

  if (!value) return I18n.t('no_label', { scope: 'fe' });

  let values;
  try { values = JSON.parse(value); } catch { values = []; }

  const map = params.colDef?.filterParams?.optionMap;

  return values
    .map((v) => {
      if (Array.isArray(options)) {
        return (options.find((o) => o?.['value'] === v) || {})['label'] || v;
      }
      return map?.get(v)?.['label'] ?? map?.get(v) ?? v;
    })
    .join(', ');
};

const selectionValueFormatter = ({value, colDef: { filterParams }}) => {
  if (Array.isArray(value)) {
    return value.map(val => filterParams.optionMap.get(val) || 'undefined').join(', ')
  }

  return filterParams.optionMap.get(value);;
}

const SELECT_COMPONENTS = ['dropdown_selection', 'radio_selection', 'radio_selection_free_text']
const MULTI_SELECT_COMPONENTS = ['checkbox_selection', 'multi_dropdown_selection', 'checkbox_selection_free_text']
const FILTER_BY_DATA_TYPE = {
  date_field: 'agDateColumnFilter',
  datetime_field: 'agDateColumnFilter',
  number_field: 'agNumberColumnFilter',
  integer_field: 'agNumberColumnFilter',
  float_field: 'agNumberColumnFilter',
  text_field: 'agTextColumnFilter',
};

const TaskDatagrid = ({
  programmeId,
  project_id,
  containerStyle,
  DatagridComponent = CommonAggridTable,
  gridProps = {},
  groupGridItems,
  reloadClick: passedReloadClick,
  permissions = {},
  advancedFeatures,
  presetId = null,
  baseQueryKey,
  programmeTasks
}) => {
  const {sorted_columns, visible_columns, task_status_options} = useTaskOptions({projectId: project_id});
  const [globalFilterVal, setGlobalFilterVal] = useState('')
  const [reloadClick, setReloadClick] = useState(0)
  const selectedRowsRef = useRef(new Map())

  useEffect(() => {
    // Init the search bar as it render in ruby
    $('#datagridGlobalFilterInput').on("keyup", function(e) {
        setGlobalFilterVal(e.target.value)
    });
  }, []);

  const specialCols = {
    identifier: (common) => ({
      ...common,
      flex: 1,
      minWidth: 100,
      cellRenderer: ProjectTaskIdCellRenderer,
    }),
    title: (common) => ({
      ...common,
      flex: 1,
      minWidth: 100,
      cellRenderer: ProjectTitleCellRenderer,
    }),
    project_identifier: (common) => ({
      ...common,
      filter: 'agTextColumnFilter',
      cellRenderer: ProjectLinkCellRenderer,
    }),
    status: (common) => ({
      ...common,
      valueGetter: (params) => {
        return (params.data?.[params.colDef.colId] || {})?.['id'];
      },
      valueFormatter: (params) => {
        return (params.data?.[params.colDef.colId] || {})?.['label'];
      },
      minWidth: 90,
      width: 120,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: task_status_options.map((o) => o.id),
        valueFormatter: (params) => {
          const item = task_status_options.find((o) => o.id == params.value);
          return item ? item.label : params.value;
        },
      },
      cellRenderer: ProjectStatusCellRenderer,
    }),
    work_package_id: () => ({
      headerName: I18n.t('work_package'),
      field: 'work_package_id',
      colId: 'work_package_id',
      rowGroup: groupGridItems,
      hide: groupGridItems,
      valueGetter: (p) => p.data?.work_package_id ?? p.data?.work_package?.id ?? null,
      cellRenderer: (params) => {
        const containsClientSideData =
          params.api.getGridOption('rowModelType') === 'serverSide' ||
          params.api.getRowGroupColumns().length === 0;

        const wp = containsClientSideData
          ? params.data?.work_package
          : params.node.allLeafChildren?.[0]?.data?.work_package;

        if (!wp?.label) return I18n.t('unassigned');

        return (
          <ShowWorkPackageLink
            triggerLabel={wp.label}
            programmeId={programmeId}
            projectId={wp.project_id}
            work_package_id={wp.id}
            permissions={permissions}
          />
        )
      },
    }),
  };

  const getDefaultFilter = (c) => FILTER_BY_DATA_TYPE[c.dataType] || 'agTextColumnFilter';

  const sorted_visible_columns = sorted_columns.map((c) => {
    if (!visible_columns.includes(c.value)) return null;

    const common = {
      field: c.label,
      colId: c.accessor,
      editable: false,
      sortable: true,
      valueGetter,
    };

    const special = specialCols[c.value];
    if (special) return special(common);

    if (SELECT_COMPONENTS.includes(c.type)) {
      return {
        ...common,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: c.value_mapping.map((op) => op.value),
          optionMap: new Map(c.value_mapping.map((op) => [op.value, op.label])),
          valueFormatter: selectionValueFormatter,
        },
        valueGetter: selectValueGetter,
        filterValueGetter: (p) => p.data?.[p.colDef?.colId]?.label
      };
    }

    if (MULTI_SELECT_COMPONENTS.includes(c.type)) {
      return {
        ...common,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: c.value_mapping.map((op) => op.value),
          optionMap: new Map(c.value_mapping.map((op) => [op.value, op.label])),
          valueFormatter: selectionValueFormatter,
        },
        valueGetter: multiSelectValueGetter,
        filterValueGetter: (p) => p.data?.[p.colDef?.colId]?.label
      };
    }

    return {
      ...common,
      filter: getDefaultFilter(c),
      cellRenderer: (params) => {
        if (params.colDef.filter === FILTER_BY_DATA_TYPE['datetime_field']) {
          return DT.dt(params.value, {datetime: true})
        } else if (params.colDef.filter === FILTER_BY_DATA_TYPE['date_field']) {
          return DT.dt(params.value)
        }
        return params.value;
      }
    };
  });
  
  const context = useMemo(() => ({
    permissions,
    advancedFeatures,
    projectId: project_id,
    programmeId: programmeId,
  }), [permissions, advancedFeatures]);

  const columnDefs = useMemo(() => [
    {
      field: '-',
      colId: 'global_search',
      editable: false,
      hide: true,
      lockVisible: true,
      filter: 'agTextColumnFilter',
      suppressColumnsToolPanel: true
    }
  ].concat(sorted_visible_columns.filter((c) => c !== null)), [groupGridItems]);

  const onRowSelected = (event) => {
    const id = event.node.data?.id;
    if (id == null) return

    if (event.node.isSelected())
      selectedRowsRef.current.set(id, event.node.data);
    else
      selectedRowsRef.current.delete(id);

    gridProps?.callbackOnRowSelected?.(Array.from(selectedRowsRef.current.values()));
  };

  const gridOptions = {
    context: context,
    onRowSelected,
    ...(gridProps?.gridOptions || {})
  }

  return (
    <div className='task-view'>
      <DatagridComponent 
        fetchPath={task_list_json_path({project_id: project_id})}
        fetchBody={{
          project_id: project_id,
          preset_id: presetId,
          programme_tasks: programmeTasks
        }}
        quickFilterText={globalFilterVal}
        columnDefs={columnDefs}
        reloadClick={passedReloadClick ?? reloadClick}
        containerStyle={containerStyle}
        {...gridProps}
        gridOptions={gridOptions}
        baseQueryKey={baseQueryKey}
      />
      <button style={{display:'none'}} id='task-datagrid-reload-button' onClick={(e) => setReloadClick(reloadClick + 1)} />
    </div>
  );
}

export default TaskDatagrid;